// medplum-consult-extras.js — ONLINE-PRIMARY browser-direct reads/writes for the
// consult surface's "extra" clinical actions, replacing five Express legs. Every
// function talks to Medplum DIRECTLY as the logged-in doctor (RLS-scoped by their
// own token via the shared getMedplum() session — see medplum-client.js), the
// same run-as-user model as medplum-writes.js / medplum-callin.js / care.js.
//
// The five ports (source Express legs in parentheses):
//   1. saveConsultTranscript / readConsultTranscript
//        (POST/GET …/consult/transcript — server/opd/consult.mjs:123-177)
//        The SACRED verbatim transcript. Stored as a FHIR Media, turns preserved
//        BYTE-EXACT. See the parity note on saveConsultTranscript.
//   2. listPatientUploads
//        (GET …/patient-uploads — server/opd/patient-portal.mjs:456-501)
//   3. linkPatientUpload / attachReportFile
//        (POST …/attach-report{,/link} — patient-portal.mjs:317-450)
//   4. recordIntakeCorrection
//        (POST …/intake/corrections — server/opd/corrections.mjs:60-163)
//        Provenance activity=CORRECT + a correction of the intake-summary
//        Composition section the consult DISPLAYS (the derived
//        MedicationStatement/AllergyIntolerance/Condition rows are neither
//        fetched into the consult graph nor, for MedicationStatement, writable
//        under the doctor RLS policy — so correcting the section is what makes a
//        remove/replace actually stick on reload).
//   5. resolveInconsistencyFlag
//        (POST …/inconsistencies/:flagId/resolve — server/opd/consistency-guard.mjs)
//   6. openConsult
//        (POST …/consult/open — consult.mjs:24-48) — READY→IN_CONSULT via the
//        shared shape/opd-state.js state machine, If-Match optimistic concurrency.
//
// FHIR-VALIDITY: Medplum rejects our internal underscore-bag fields (_healther,
// _transcript, _encounterId) — they are not FHIR. So, exactly like medplum-store's
// mirror (server/opd/medplum-store.mjs encodeHealtherBlob) and medplum-photo.js,
// each bag is encoded into a JSON `valueString` extension and the encounter link
// rides FHIR-natively (encounter/context/target). The `_transcript` payload — which
// main-1 kept DISK-ONLY (it is in the server's UNSYNCABLE_LOCAL_KEYS, so the turns
// NEVER reached Medplum) — is here persisted to Medplum for the first time, byte-
// exact, under its own extension so the verbatim record survives the disk's removal.
import { getMedplum } from './medplum-client.js';
import { getOutbox } from './outbox.js';
import { resourceCreateEntry } from './outbox-ops.js';
import { buildTransitionUpdate, currentState, STATES } from './shape/opd-state.js';

// The shared opd-healther bag extension (session/metadata) — same URL the server
// mirror and every other client lib use, so existing decoders recover it.
const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';
// The verbatim transcript payload (turns + fullText). A dedicated extension keeps
// the SACRED byte-exact turns cleanly recoverable and separate from metadata.
const EXT_OPD_TRANSCRIPT = 'http://healther.io/fhir/StructureDefinition/opd-consult-transcript';
// Per-save conditional-create key system for the transcript Media (Media carries
// a FHIR identifier, so the outbox's createResourceIfNoneExist can dedupe a
// crash-replay of the SAME queued entry — a Provenance could not, having no
// identifier element). Each distinct save gets a distinct key, so consecutive
// saves stay distinct Media (newest = the full transcript), never coalescing.
const TRANSCRIPT_KEY_SYSTEM = 'https://healther.io/opd/consult-transcript-key';

// Harness seam: a Node/vitest test injects an in-memory-backed Outbox via
// globalThis.__HEALTHER_HARNESS_OUTBOX__ so it never touches IndexedDB (mirrors
// medplum-writes.resolveOutbox). The app always gets the real singleton.
async function resolveOutbox() {
  if (typeof globalThis !== 'undefined' && globalThis.__HEALTHER_HARNESS_OUTBOX__) {
    return globalThis.__HEALTHER_HARNESS_OUTBOX__;
  }
  return getOutbox();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isMedplumUuid(v) { return UUID_RE.test(String(v || '')); }

// ── bag encode/decode (FHIR-validity shim, mirrors medplum-store encodeHealtherBlob) ──
function bagExt(url, obj) { return { url, valueString: JSON.stringify(obj) }; }
function decodeBag(resource, url) {
  const e = ((resource && resource.extension) || []).find((x) => x && x.url === url && x.valueString);
  if (!e) return null;
  try { return JSON.parse(e.valueString); } catch { return null; }
}
// The `_healther` bag off a resource — decoded copy (or the raw field on a
// disk-shaped row), {} when absent, matching medplum-analytics.decodeHealther.
function decodeHealther(resource) {
  if (resource && resource._healther) return resource._healther;
  return decodeBag(resource, EXT_OPD_HEALTHER) || {};
}

// A 412 Precondition-Failed (optimistic-concurrency loss) — same detection idiom
// as outbox.js is412 (outbox.js:33-36).
function is412(e) {
  return !!(e && (e.status === 412
    || /precondition/i.test(e.message || '')
    || /412/.test(String((e.outcome && e.outcome.id) || ''))));
}

// updateResource with an If-Match on the resource's own meta.versionId (weak
// ETag), the optimistic-concurrency CAS. Mirrors outbox.js:247-248.
function updateWithIfMatch(medplum, resource) {
  const versionId = resource && resource.meta && resource.meta.versionId;
  const opts = versionId ? { headers: { 'If-Match': 'W/"' + versionId + '"' } } : undefined;
  return medplum.updateResource(resource, opts);
}

// ════════════════════════════════════════════════════════════════════════════
// 1. CONSULT TRANSCRIPT — the SACRED verbatim record (byte-exact turns)
// ════════════════════════════════════════════════════════════════════════════

// Clean one raw turn EXACTLY as the Express leg does (consult.mjs:136-142) so a
// client-written transcript is byte-identical to a server-written one. `text` is
// String()'d + trim()'d — the ONLY normalisation; the verbatim script (Devanagari,
// Tamil, mixed) is preserved through JSON, never transliterated or stripped.
function cleanTurns(turns) {
  return (Array.isArray(turns) ? turns : []).map((t) => ({
    speaker: String((t && t.speaker) || 'Unknown'),
    text: String((t && t.text) || '').trim(),
    startMs: Number.isFinite(Number(t && t.startMs)) ? Number(t.startMs) : null,
    endMs: Number.isFinite(Number(t && t.endMs)) ? Number(t.endMs) : null,
    confidence: typeof (t && t.confidence) === 'number' ? t.confidence : null,
  })).filter((t) => t.text);
}

/**
 * Persist a live-capture consult transcript as a FHIR Media on the encounter.
 * Returns { ok, media, turns } — DTO parity with the Express leg's
 * res.json({ ok:true, media, turns:<count> }).
 *
 * @param {object} args
 * @param {string}  args.encounterId  Medplum Encounter UUID
 * @param {object} [args.subject]     Encounter.subject to copy onto the Media; if
 *                                    omitted it is read from the encounter.
 * @param {Array}   args.turns        [{ speaker, text, startMs, endMs, confidence }]
 * @param {string} [args.lang='multi']
 * @param {string} [args.source='opd-voice']
 *
 * DURABLE + OFFLINE-CAPABLE: the Media create is routed through the offline write
 * outbox (the SACRED verbatim must survive a transient/offline failure, not be
 * swallowed). Online, enqueue() drains synchronously and returns the landed row;
 * offline, the row sits 'queued' and replays on reconnect. Returns
 * { ok:true, queued, media, turns } — `queued:true` means captured on this device
 * and awaiting sync (still a success). A hard rejection (non-retryable 4xx) is
 * FAIL LOUD: it throws so the caller never reports the record as saved.
 */
export async function saveConsultTranscript({ encounterId, subject, turns, lang, source } = {}) {
  if (!encounterId) throw new Error('saveConsultTranscript: encounterId is required');
  const cleanedTurns = cleanTurns(turns);
  if (!cleanedTurns.length) throw new Error('saveConsultTranscript: turns required');
  const { medplum } = await getMedplum();

  // Copy the Encounter.subject so the Media joins to the patient (FHIR-native),
  // reading the encounter only when the caller didn't already hand it in.
  let subj = subject || null;
  if (!subj && isMedplumUuid(encounterId)) {
    try { const enc = await medplum.readResource('Encounter', encounterId); subj = enc && enc.subject; } catch (_) { /* subject is nice-to-have */ }
  }

  const fullText = cleanedTurns.map((t) => `${t.speaker}: ${t.text}`).join('\n');
  const durationMs = cleanedTurns.reduce((m, t) => Math.max(m, t.endMs || 0), 0);
  const recordedAt = new Date().toISOString();
  const lg = lang || 'multi';
  const src = source || 'opd-voice';
  // A distinct key per save (uuid + capture instant) — unique so consecutive
  // saves stay distinct Media (the newest is the full transcript), stable within
  // this entry so a crash-replay dedupes via createResourceIfNoneExist.
  const transcriptKey = `${encounterId}-${Date.now()}`;

  const media = {
    resourceType: 'Media',
    status: 'completed',
    // FHIR identifier so the outbox's conditional-create can dedupe a replay.
    identifier: [{ system: TRANSCRIPT_KEY_SYSTEM, value: transcriptKey }],
    type: { coding: [{ code: 'consult-transcript' }] },
    ...(subj ? { subject: subj } : {}),
    encounter: { reference: `Encounter/${encounterId}` },
    content: { contentType: 'application/json', title: 'Live consult transcript' },
    extension: [
      // Byte-exact verbatim payload (turns + fullText). Parity with the server's
      // disk-only `_transcript` bag (consult.mjs:158-165), now Medplum-durable.
      bagExt(EXT_OPD_TRANSCRIPT, {
        transcript: fullText, turns: cleanedTurns, durationMs, lang: lg, source: src, recordedAt,
      }),
      // Session metadata bag (parity with the server's `_healther`, consult.mjs:166-171).
      bagExt(EXT_OPD_HEALTHER, {
        sessionType: 'opd-consult', recordedAt, turns: cleanedTurns.length, source: src,
      }),
    ],
  };

  // The outbox replays as the enqueuing user; a clinical write with no known
  // author must not queue (it could replay under whoever logs in next — §2). The
  // ASYNC profile form (getProfileAsync) waits out the post-resume race the SYNC
  // getProfile() would lose. Fail loud on absence, matching medplum-writes.js.
  const profile = await medplum.getProfileAsync();
  if (!profile) throw new Error('saveConsultTranscript: no logged-in profile — refusing to queue the verbatim record with no known author');

  const outbox = await resolveOutbox();
  const landed = await outbox.enqueue(resourceCreateEntry({
    body: media, slug: transcriptKey, slugSystem: TRANSCRIPT_KEY_SYSTEM, profileId: profile.id,
  }));
  // A non-retryable rejection (422/403) parks 'failed'; a create carries no
  // If-Match so 'conflict' is not expected — treat either as a hard failure and
  // FAIL LOUD so the caller never claims the sacred record was saved.
  if (landed && landed.status === 'failed') {
    throw new Error(landed.lastError || 'consult transcript was rejected by Medplum');
  }
  if (landed && landed.status === 'conflict') {
    throw new Error(landed.lastError || 'consult transcript write conflicted');
  }
  // 'queued' (genuinely offline) is a SUCCESS: captured durably, replays on
  // reconnect. Otherwise it landed 'done' (or 'done'+GC'd → undefined) online.
  const queued = !!(landed && landed.status === 'queued');
  const savedId = landed ? (landed.resolvedId || null) : null;
  return { ok: true, queued, media: savedId ? { ...media, id: savedId } : media, turns: cleanedTurns.length };
}

/**
 * Decode a transcript Media back to its verbatim payload
 * { transcript, turns, durationMs, lang, source, recordedAt } — or null when the
 * Media carries no transcript extension. Falls back to a disk-shaped `_transcript`
 * field for a row that never round-tripped through Medplum. PURE.
 */
export function decodeConsultTranscript(media) {
  if (!media) return null;
  return decodeBag(media, EXT_OPD_TRANSCRIPT) || media._transcript || null;
}

/** True iff a Media is a consult transcript (either the FHIR code or the decoded
 *  session-type bag — matches the server's dual check, consult.mjs:198-201). */
function isConsultTranscript(m) {
  if (m && m.type && m.type.coding && m.type.coding[0] && m.type.coding[0].code === 'consult-transcript') return true;
  return decodeHealther(m).sessionType === 'opd-consult';
}

/**
 * Read the most-recent consult transcript for an encounter and decode it.
 * Returns the decoded payload (see decodeConsultTranscript) or null.
 */
export async function readConsultTranscript(encounterId) {
  if (!encounterId) return null;
  const { medplum } = await getMedplum();
  const media = await medplum.searchResources('Media', `encounter=Encounter/${encounterId}&_count=200`);
  const transcripts = (Array.isArray(media) ? Array.from(media) : []).filter(isConsultTranscript);
  const latest = transcripts[transcripts.length - 1];
  return latest ? decodeConsultTranscript(latest) : null;
}

// ════════════════════════════════════════════════════════════════════════════
// 2/3. PATIENT UPLOADS — read, link, attach (extends medplum-photo.js patterns)
// ════════════════════════════════════════════════════════════════════════════

// A genuine patient-phone upload: type patient-upload AND neither origin (staff
// attach) nor kind (linked copy) set on its bag. Parity with the server's
// isPortalPatientUpload (patient-portal.mjs:147-151), reading the decoded bag.
function isPortalPatientUpload(r) {
  if (!r || !r.type || !r.type.coding || !r.type.coding[0] || r.type.coding[0].code !== 'patient-upload') return false;
  const h = decodeHealther(r);
  if (h.origin || h.kind) return false;
  return true;
}

// One DocumentReference → the upload-picker row DTO. Parity with the server's
// uploadRowFromDocRef (patient-portal.mjs:153-169).
function uploadRowFromDocRef(r, linkedFromIds) {
  const att = (r.content && r.content[0] && r.content[0].attachment) || {};
  const eid = (r.context && r.context.encounter && r.context.encounter[0] && r.context.encounter[0].reference
    && r.context.encounter[0].reference.replace(/^Encounter\//, ''))
    || (r.encounter && r.encounter.reference && r.encounter.reference.replace(/^Encounter\//, ''))
    || null;
  return {
    id: r.id,
    title: att.title || 'Patient upload',
    url: att.url || null,
    contentType: att.contentType || null,
    uploadedAt: (r.meta && r.meta.lastUpdated) || r.date || null,
    encounterId: eid,
    alreadyAttached: linkedFromIds.has(r.id),
  };
}

/**
 * All genuine patient-phone uploads for this encounter's PATIENT (the "from
 * patient's phone" picker). Excludes staff-attached priors and already-linked
 * copies. Returns { ok, encounterId, patientId, uploads } — parity with the
 * Express leg (patient-portal.mjs:456-501).
 *
 * RLS: the DocumentReference?patient search is caller-scoped by the doctor's own
 * token, so it can only ever return uploads for a patient they may read.
 */
export async function listPatientUploads({ encounterId, patientId } = {}) {
  if (!encounterId) throw new Error('listPatientUploads: encounterId is required');
  const { medplum } = await getMedplum();

  let pid = patientId || null;
  if (!pid && isMedplumUuid(encounterId)) {
    try {
      const enc = await medplum.readResource('Encounter', encounterId);
      pid = enc && enc.subject && enc.subject.reference ? enc.subject.reference.split('/').pop() : null;
    } catch (_) { /* fall through — no patient scope */ }
  }

  // Docs already linked into THIS encounter (kind prior-report) → mark their
  // source rows alreadyAttached, matching the server's linkedFromIds set.
  const encDocs = await medplum.searchResources('DocumentReference', `encounter=Encounter/${encounterId}&_count=200`);
  const linkedFromIds = new Set(
    (Array.isArray(encDocs) ? encDocs : [])
      .map((d) => decodeHealther(d))
      .filter((h) => h.kind === 'prior-report' && h.linkedFrom)
      .map((h) => h.linkedFrom),
  );

  const uploads = [];
  const seen = new Set();
  if (pid) {
    const docs = await medplum.searchResources('DocumentReference', `patient=Patient/${pid}&_count=200`);
    for (const r of (Array.isArray(docs) ? docs : [])) {
      if (seen.has(r.id) || !isPortalPatientUpload(r)) continue;
      seen.add(r.id);
      uploads.push(uploadRowFromDocRef(r, linkedFromIds));
    }
  }
  uploads.sort((a, b) => String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
  return { ok: true, encounterId, patientId: pid, uploads };
}

/**
 * Link an EXISTING patient-upload DocumentReference into this encounter as an
 * attached prior report. Does NOT duplicate the file — the new DocRef points at
 * the same attachment URL. Returns { documentReference, ocr, alreadyAttached }
 * — parity with the Express leg (patient-portal.mjs:370-450).
 *
 * OCR PARITY NOTE: the server best-effort re-OCRs a source that lacks OCR
 * (runOcrBestEffort needs the OpenAI key + the file bytes). That does NOT run on
 * a browser-direct write — we carry only the source's EXISTING ocr/summary bag
 * (Card-E-style drop, same as medplum-photo.js dropping vision).
 *
 * CROSS-PATIENT GUARD: the source DocRef id is client-supplied. We confirm it
 * belongs to the SAME patient as the target encounter before copying it in
 * (parity with the server's targetPatientId===srcPatientId check). Fails closed.
 */
export async function linkPatientUpload({ encounterId, documentReferenceId, attachedBy } = {}) {
  if (!encounterId) throw new Error('linkPatientUpload: encounterId is required');
  if (!documentReferenceId) throw new Error('linkPatientUpload: documentReferenceId is required');
  const { medplum } = await getMedplum();

  // Read the source AS THE CALLER — an RLS-denied read throws 403/404, which IS
  // the patient boundary.
  const source = await medplum.readResource('DocumentReference', documentReferenceId);

  // Resolve both patients and refuse a cross-patient link. subject is the
  // FHIR-native home; fall back to the source's owning encounter when absent.
  const targetEnc = await medplum.readResource('Encounter', encounterId);
  const targetPatientId = targetEnc && targetEnc.subject && targetEnc.subject.reference
    ? targetEnc.subject.reference.split('/').pop() : null;
  let srcPatientId = source && source.subject && source.subject.reference
    ? source.subject.reference.split('/').pop() : null;
  if (!srcPatientId) {
    const srcEncId = (source.context && source.context.encounter && source.context.encounter[0]
      && source.context.encounter[0].reference && source.context.encounter[0].reference.split('/').pop())
      || (source.encounter && source.encounter.reference && source.encounter.reference.split('/').pop())
      || null;
    if (srcEncId) {
      try {
        const srcEnc = await medplum.readResource('Encounter', srcEncId);
        srcPatientId = srcEnc && srcEnc.subject && srcEnc.subject.reference ? srcEnc.subject.reference.split('/').pop() : null;
      } catch (_) { /* fail closed below */ }
    }
  }
  if (!targetPatientId || !srcPatientId || srcPatientId !== targetPatientId) {
    throw Object.assign(new Error('cross-patient-link-denied'), { status: 403 });
  }

  // Idempotent: re-linking the same source returns the existing link.
  const encDocs = await medplum.searchResources('DocumentReference', `encounter=Encounter/${encounterId}&_count=200`);
  const existing = (Array.isArray(encDocs) ? encDocs : []).find((d) => {
    const h = decodeHealther(d);
    return h.kind === 'prior-report' && h.linkedFrom === documentReferenceId;
  });
  const srcBag = decodeHealther(source);
  if (existing) {
    return { documentReference: existing, ocr: decodeHealther(existing).ocr || srcBag.ocr || null, alreadyAttached: true };
  }

  const att = (source.content && source.content[0] && source.content[0].attachment) || {};
  const ocr = srcBag.ocr || null;
  const summary = srcBag.summary || null;
  const encRef = `Encounter/${encounterId}`;
  const docRef = await medplum.createResource({
    resourceType: 'DocumentReference',
    status: 'current',
    type: { coding: [{ code: 'patient-upload' }] },
    subject: { reference: `Patient/${targetPatientId}` },
    context: { encounter: [{ reference: encRef }] },
    date: new Date().toISOString(),
    content: [{ attachment: {
      contentType: att.contentType || null,
      url: att.url || null,
      title: att.title || 'Patient upload',
    } }],
    extension: [bagExt(EXT_OPD_HEALTHER, {
      kind: 'prior-report',
      source: 'patient-upload',
      linkedFrom: documentReferenceId,
      attachedBy: attachedBy || 'staff',
      ...(ocr ? { ocr } : {}),
      ...(summary ? { summary } : {}),
    })],
  });
  medplum.invalidateSearches('DocumentReference');
  return { documentReference: docRef, ocr, alreadyAttached: false };
}

/**
 * Attach a NEW file (the doctor uploads a prior report the patient brought in) —
 * Binary bytes first, then a patient-upload DocumentReference pointing at it.
 * Extends medplum-photo.js's createBinary→createResource pattern. Returns
 * { documentReference, ocr:null } — parity with the Express leg
 * (patient-portal.mjs:317-364), minus server-side OCR/vision (dropped, same as
 * medplum-photo.js drops vision — a browser write can't run the OpenAI key path).
 */
export async function attachReportFile({ encounterId, patientId, file, title, source, attachedBy, contentType, filename } = {}) {
  if (!encounterId) throw new Error('attachReportFile: encounterId is required');
  if (!file) throw new Error('attachReportFile: file is required');
  const { medplum } = await getMedplum();

  let pid = patientId || null;
  if (!pid && isMedplumUuid(encounterId)) {
    const enc = await medplum.readResource('Encounter', encounterId);
    pid = enc && enc.subject && enc.subject.reference ? enc.subject.reference.split('/').pop() : null;
  }
  const type = contentType || file.type || 'application/octet-stream';
  const ttl = title || (file && file.name) || 'Prior report';
  const binary = await medplum.createBinary({ data: file, contentType: type, filename: filename || ttl });
  const encRef = `Encounter/${encounterId}`;
  const docRef = await medplum.createResource({
    resourceType: 'DocumentReference',
    status: 'current',
    type: { coding: [{ code: 'patient-upload' }] },
    ...(pid ? { subject: { reference: `Patient/${pid}` } } : {}),
    context: { encounter: [{ reference: encRef }] },
    date: new Date().toISOString(),
    content: [{ attachment: { contentType: type, url: `Binary/${binary.id}`, title: ttl } }],
    extension: [bagExt(EXT_OPD_HEALTHER, {
      origin: 'external-prior',
      attachedBy: attachedBy || 'staff',
      category: 'other',
      categorySource: 'device-default',
      ...(source ? { source } : {}),
    })],
  });
  medplum.invalidateSearches('DocumentReference');
  return { documentReference: docRef, ocr: null };
}

// ════════════════════════════════════════════════════════════════════════════
// 4b. DOCTOR-CONFIRMED ALLERGY — a real AllergyIntolerance from a consult ＋Add
// ════════════════════════════════════════════════════════════════════════════

// Per-(patient, substance) conditional-create key. AllergyIntolerance carries a
// FHIR identifier, so the outbox's createResourceIfNoneExist can dedupe a
// crash-replay AND a doctor re-confirming the same allergy — never a twin.
const ALLERGY_KEY_SYSTEM = 'https://healther.io/opd/allergy-key';

/**
 * Persist a doctor-CONFIRMED allergy (accepted via a consult ＋Add on an allergy
 * addition card) as a real AllergyIntolerance, browser-direct as the logged-in
 * doctor (RLS) and through the offline OUTBOX (durable + offline-capable). This
 * replaces the old silent-discard: the ＋Add used to drop the card with no write.
 *
 * The row-level doctor policy (opd-doctor-rowlevel) permits AllergyIntolerance
 * create on a patient in the doctor's department compartment — Medplum inherits
 * meta.accounts from the referenced Patient BEFORE the write-policy check, so a
 * fresh row matches `AllergyIntolerance?_compartment=%department` at create (the
 * same idiom the sign Bot's FamilyMemberHistory write already relies on). No new
 * policy arm is needed.
 *
 * Returns { ok, queued, allergy } — `queued:true` means captured offline and
 * awaiting sync (still a success). Fails loud (throws) on a hard rejection.
 */
export async function addConfirmedAllergy({ patientId, encounterId, name, doctor } = {}) {
  if (!patientId) throw new Error('addConfirmedAllergy: patientId is required');
  const substance = String(name || '').trim();
  if (!substance) throw new Error('addConfirmedAllergy: allergy name is required');
  const { medplum } = await getMedplum();
  // URL-safe conditional-create key (the outbox does not encode the identifier
  // query, so keep it to [a-z0-9-]): patient UUID + normalised substance.
  const key = `${patientId}-${substance.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;

  const allergy = {
    resourceType: 'AllergyIntolerance',
    identifier: [{ system: ALLERGY_KEY_SYSTEM, value: key }],
    clinicalStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical', code: 'active', display: 'Active' }] },
    verificationStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification', code: 'confirmed', display: 'Confirmed' }] },
    code: { text: substance },
    patient: { reference: `Patient/${patientId}` },
    ...(encounterId && isMedplumUuid(encounterId) ? { encounter: { reference: `Encounter/${encounterId}` } } : {}),
    recordedDate: new Date().toISOString(),
    ...(doctor ? { recorder: { display: doctor } } : {}),
    extension: [bagExt(EXT_OPD_HEALTHER, { source: 'consult-confirmed', addedBy: doctor || 'Doctor' })],
  };

  // The outbox replays as the enqueuing user; a clinical write with no known
  // author must not queue (§2 wrong-user hole). Fail loud on absence.
  const profile = await medplum.getProfileAsync();
  if (!profile) throw new Error('addConfirmedAllergy: no logged-in profile — refusing to queue a clinical write with no known author');
  const outbox = await resolveOutbox();
  const landed = await outbox.enqueue(resourceCreateEntry({
    body: allergy, slug: key, slugSystem: ALLERGY_KEY_SYSTEM, profileId: profile.id,
  }));
  if (landed && landed.status === 'failed') {
    throw new Error(landed.lastError || 'allergy was rejected by Medplum');
  }
  if (landed && landed.status === 'conflict') {
    throw new Error(landed.lastError || 'allergy write conflicted');
  }
  const queued = !!(landed && landed.status === 'queued');
  const savedId = landed ? (landed.resolvedId || null) : null;
  return { ok: true, queued, allergy: savedId ? { ...allergy, id: savedId } : allergy };
}

// ════════════════════════════════════════════════════════════════════════════
// 4. INTAKE CORRECTION — Provenance activity=CORRECT + Observation status flip
// ════════════════════════════════════════════════════════════════════════════

// Auto-infer the error class from the conflict shape. VERBATIM from the server's
// errorClassFor (corrections.mjs:34-39).
export function errorClassFor(c = {}) {
  if (c.action === 'remove') return 'entered-in-error';
  if (c.kind === 'omission') return 'missed-omission';
  if (c.kind === 'mismatch') return 'clinical-correction';
  return 'enrichment';
}

// The per-(encounter, conflict, action) idempotency key. VERBATIM from the
// server's correctionKey (corrections.mjs:41-43).
function correctionKey(encounterId, c) {
  return `${encounterId}:${c.conflictId || c.id || c.field || 'x'}:${c.action || 'replace'}`;
}

// Field → the intake-summary Composition SECTION whose structured items the
// consult actually DISPLAYS. shape/encounter-view.js shapeIntakeSections reads
// current meds / allergies / past conditions from THIS section's payload — NOT
// from the derived MedicationStatement / AllergyIntolerance / Condition rows
// the intake pipeline also writes (server/opd/intake-extraction.mjs). Those
// derived rows are not fetched into the consult encounter graph
// (medplum-encounter.js REVINCLUDES), and the doctor's row-level AccessPolicy
// (opd-doctor-rowlevel) does not even grant MedicationStatement — so a
// correction only STICKS on reload if this section is corrected. The value is
// the item fields that hold the human-readable text to match a removal against.
const EXT_SECTION_PAYLOAD = 'https://healther.io/opd/section-payload';
const EXT_SECTION_PAYLOAD_LEGACY = 'http://healther.io/fhir/StructureDefinition/opd-section-payload';
const INTAKE_SECTION_ITEM_FIELDS = {
  current_medications: ['name'],
  allergies: ['name', 'allergen'],
  past_conditions: ['condition', 'name'],
};

function normForMatch(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }
function escapeSectionXml(s) { return String(s == null ? '' : s).replace(/[<>&]/g, (ch) => (ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&amp;')); }

// Candidate strings for ONE section item — its named fields PLUS a compound
// "name dose freq route timing" label (the exact shape ConsultCompact builds
// its removable med chip from, see curMeds), so a remove whose intakeSays is
// that compound label still matches the structured item it was derived from.
function sectionItemCandidates(item, fields) {
  const out = [];
  for (const f of fields) if (item && item[f]) out.push(String(item[f]));
  const compound = [item && item.name, item && (item.dose || item.strength),
    item && (item.frequency || item.freq), item && item.route,
    item && (item.timing || item.when || item.lastTaken), item && (item.duration || item.since)]
    .filter(Boolean).join(' ');
  if (compound) out.push(compound);
  return out;
}
function sectionItemMatches(item, fields, want) {
  const w = normForMatch(want);
  if (!w) return false;
  for (const cand of sectionItemCandidates(item, fields)) {
    const c = normForMatch(cand);
    if (c && (c === w || w.includes(c) || c.includes(w))) return true;
  }
  return false;
}

/**
 * Correct the DISPLAY SOURCE — the intake-summary Composition's section — so a
 * doctor's remove/replace actually persists to what the consult re-derives on
 * reload. Written AS THE DOCTOR with If-Match optimistic concurrency
 * (Composition update IS in the opd-doctor-rowlevel policy — unlike
 * MedicationStatement, which the policy omits entirely). Returns
 * { reference:'Composition/…' } when it changed a row, or null when there was
 * nothing to correct (the item was added in-consult, not from intake — a
 * genuine no-op, not a failure). THROWS on a real write failure so the caller
 * fails loud and never claims a removal that was lost.
 */
async function correctIntakeSection(medplum, encounterId, field, action, want, replaceWith) {
  const fields = INTAKE_SECTION_ITEM_FIELDS[field];
  if (!fields) return null; // only the item-list sections are corrected here
  const comps = await medplum.searchResources('Composition', `encounter=Encounter/${encounterId}&_count=200`);
  const intake = (Array.isArray(comps) ? comps : [])
    .filter((c) => (c.type && c.type.coding && c.type.coding[0] && c.type.coding[0].code === 'intake-summary') || decodeHealther(c).kind === 'intake-summary')
    .sort((a, b) => String((b.date || (b.meta && b.meta.lastUpdated)) || '').localeCompare(String((a.date || (a.meta && a.meta.lastUpdated)) || '')))[0];
  if (!intake || !Array.isArray(intake.section)) return null;
  const secIdx = intake.section.findIndex((s) => s && s.code && s.code.coding && s.code.coding[0] && s.code.coding[0].code === field);
  if (secIdx < 0) return null;
  const sec = intake.section[secIdx];
  const payloadExt = (sec.extension || []).find((e) => e && (e.url === EXT_SECTION_PAYLOAD || e.url === EXT_SECTION_PAYLOAD_LEGACY) && e.valueString);
  let p; try { p = payloadExt ? (JSON.parse(payloadExt.valueString) || {}) : {}; } catch { p = {}; }
  if (!Array.isArray(p.items) || !p.items.length) return null;
  const before = p.items.length;
  let changed = false;
  if (action === 'remove') {
    const kept = p.items.filter((it) => !sectionItemMatches(it, fields, want));
    if (kept.length !== before) { p.items = kept; changed = true; }
  } else if (action === 'replace' && replaceWith) {
    p.items = p.items.map((it) => {
      if (!changed && sectionItemMatches(it, fields, want)) { changed = true; return { ...it, [fields[0]]: replaceWith }; }
      return it;
    });
  }
  if (!changed) return null;
  // Regenerate the section narrative (FHIR cmp-1 requires each section carry
  // text/entry/sub-section) exactly as intake-extraction builds it: join the
  // remaining item names; 'None' once the last one was removed.
  const summary = p.items.length
    ? p.items.map((i) => (i && (i[fields[0]] || i.name || i.condition || i.text)) || '').filter(Boolean).join('; ')
    : 'None';
  const nextExt = (sec.extension || []).map((e) => ((e && (e.url === EXT_SECTION_PAYLOAD || e.url === EXT_SECTION_PAYLOAD_LEGACY)) ? { ...e, valueString: JSON.stringify(p) } : e));
  if (!payloadExt) nextExt.push({ url: EXT_SECTION_PAYLOAD, valueString: JSON.stringify(p) });
  const nextSection = intake.section.slice();
  nextSection[secIdx] = { ...sec, text: { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml">${escapeSectionXml(summary)}</div>` }, extension: nextExt };
  const updated = await updateWithIfMatch(medplum, { ...intake, section: nextSection });
  medplum.invalidateSearches('Composition');
  return { reference: `Composition/${(updated && updated.id) || intake.id}` };
}

/**
 * Persist one intake-correction: a Provenance (activity=CORRECT) recording
 * aiSaid→doctorSaid, PLUS — for the item-list intake sections (current
 * medications, allergies, past conditions) — a correction of the intake-summary
 * Composition SECTION the consult actually displays (correctIntakeSection),
 * which is what makes a remove/replace stick on reload. The patient verbatim
 * (QuestionnaireResponse step text + the transcript Media) is never touched, so
 * §2 "patient verbatim is sacred" holds — only the AI-drafted summary is
 * corrected, and the Provenance preserves the removed value (aiSaid). Returns
 * { ok, recorded, provenanceId, errorClass }. An enrichment/addition is a no-op
 * (recorded:false) — that's an append, not a captured mistake.
 *
 * @param {object} args
 * @param {string} args.encounterId
 * @param {object} args.correction  { conflictId?, field?, kind?, action?, intakeSays?, consultSays? }
 * @param {string} [args.doctor='Doctor']
 */
export async function recordIntakeCorrection({ encounterId, correction, doctor } = {}) {
  if (!encounterId) throw new Error('recordIntakeCorrection: encounterId is required');
  const c = correction || {};
  const errorClass = errorClassFor(c);
  // Additions/enrichment are not a captured-wrong event (unless it's a remove).
  if ((c.kind === 'addition' || errorClass === 'enrichment') && c.action !== 'remove') {
    return { ok: true, recorded: false, provenanceId: null, errorClass };
  }
  const { medplum } = await getMedplum();
  const key = correctionKey(encounterId, c);

  // Idempotency: a Provenance already recorded for this (encounter, conflict,
  // action)? Provenance links its encounter via target[], so search by target.
  const priorProvs = await medplum.searchResources('Provenance', `target=Encounter/${encounterId}&_count=200`);
  const existing = (Array.isArray(priorProvs) ? priorProvs : []).find((p) => decodeHealther(p).correctionKey === key);
  if (existing) return { ok: true, recorded: true, provenanceId: existing.id, errorClass };

  // Correct the DISPLAY SOURCE the consult re-derives from on reload: the
  // intake-summary Composition SECTION (see correctIntakeSection). The old code
  // searched Observation here — WRONG for the item sections: intake stores
  // current meds as MedicationStatement, allergies as AllergyIntolerance, past
  // conditions as Condition (server/opd/intake-extraction.mjs), so a med remove
  // matched no Observation and silently no-op'd, and the removed med reappeared
  // from the intake Composition on every reload. Even a correctly-flagged
  // MedicationStatement would not help — it is not fetched into the consult
  // graph and the doctor RLS policy (opd-doctor-rowlevel) does not grant it.
  // Correcting the section is the in-policy write that actually makes it stick.
  let sourceRef = null;
  const want = String(c.intakeSays || '').trim();
  if (want && INTAKE_SECTION_ITEM_FIELDS[c.field]) {
    // FAIL LOUD: a real write failure throws here (the caller rolls back and
    // never claims a removal that was lost). A clean "nothing matched" returns
    // null — the item was added in-consult, not from intake, so there is
    // genuinely nothing in the intake summary to correct.
    sourceRef = await correctIntakeSection(
      medplum, encounterId, c.field, c.action,
      want, c.action === 'replace' ? (c.consultSays == null ? '' : String(c.consultSays)) : null,
    );
  }

  const recorded = new Date().toISOString();
  const prov = await medplum.createResource({
    resourceType: 'Provenance',
    target: [
      { reference: `Encounter/${encounterId}` },
      ...(sourceRef ? [sourceRef] : []),
    ],
    recorded,
    // HL7 v3 DataOperation "CORRECT" — verbatim from corrections.mjs:104.
    activity: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-DataOperation', code: 'CORRECT', display: 'correct' }] },
    agent: [{
      type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type', code: 'author', display: 'Author' }] },
      who: { display: doctor || 'Doctor' },
    }],
    ...(sourceRef ? { entity: [{ role: 'source', what: sourceRef }] } : {}),
    extension: [bagExt(EXT_OPD_HEALTHER, {
      kind: 'intake-correction',
      correctionKey: key,
      field: c.field || null,
      conflictKind: c.kind || null,
      action: c.action || 'replace',
      errorClass,
      aiSaid: c.intakeSays == null ? null : c.intakeSays,
      doctorSaid: c.consultSays == null ? null : c.consultSays,
      correctedSource: sourceRef ? sourceRef.reference : null,
      recorded,
    })],
  });
  medplum.invalidateSearches('Provenance');
  return { ok: true, recorded: true, provenanceId: prov.id, errorClass };
}

// ════════════════════════════════════════════════════════════════════════════
// 5. INCONSISTENCY FLAG RESOLVE — a flag-resolve Provenance on the patient
// ════════════════════════════════════════════════════════════════════════════

const FLAG_RESOLVE_ACTIVITY = 'flag-resolve';       // consistency-guard.mjs:43
const INCONSISTENCY_KIND = 'consistency-flag-resolution'; // consistency-guard.mjs:44

/**
 * Resolve a data-inconsistency flag by writing a flag-resolve Provenance
 * targeting the patient (and its anchor encounter). Idempotent — a second call
 * for a flag already resolved returns { ok, alreadyResolved:true } and writes
 * nothing. Returns { ok } | { ok, alreadyResolved } — parity with
 * resolveInconsistency (consistency-guard.mjs:343-400).
 */
export async function resolveInconsistencyFlag({ patientId, flagId, resolution, resolvedBy } = {}) {
  if (!patientId || !flagId) return { ok: false, error: 'patientId and flagId required' };
  const { medplum } = await getMedplum();

  // Idempotency: scan the patient's flag-resolve Provenance for this flag.
  const patientProvs = await medplum.searchResources('Provenance', `target=Patient/${patientId}&_count=200`);
  const already = (Array.isArray(patientProvs) ? patientProvs : []).some((p) => {
    const h = decodeHealther(p);
    return h.kind === INCONSISTENCY_KIND && h.flagId === flagId;
  });
  if (already) return { ok: true, alreadyResolved: true };

  // Anchor encounter: any of the patient's encounters (server anchorEncounter
  // picks one for the target/audit link).
  let anchorId = null;
  try {
    const encs = await medplum.searchResources('Encounter', `subject=Patient/${patientId}&_count=1`);
    anchorId = (Array.isArray(encs) && encs[0] && encs[0].id) || null;
  } catch (_) { /* the Patient target alone is a valid anchor */ }

  const recorded = new Date().toISOString();
  try {
    await medplum.createResource({
      resourceType: 'Provenance',
      target: [
        { reference: `Patient/${patientId}` },
        ...(anchorId ? [{ reference: `Encounter/${anchorId}` }] : []),
      ],
      recorded,
      activity: { coding: [{ system: 'http://healther.io/CodeSystem/opd-activity', code: FLAG_RESOLVE_ACTIVITY, display: 'flag-resolve' }] },
      agent: [{
        type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type', code: 'author', display: 'Author' }] },
        who: { display: resolvedBy || 'Doctor' },
      }],
      extension: [bagExt(EXT_OPD_HEALTHER, {
        kind: INCONSISTENCY_KIND,
        flagId,
        patientId,
        resolution: resolution || 'acknowledged',
        resolvedBy: resolvedBy || 'Doctor',
        recorded,
      })],
    });
    medplum.invalidateSearches('Provenance');
  } catch (_) {
    return { ok: false, error: 'persist-failed' };
  }
  return { ok: true };
}

// ════════════════════════════════════════════════════════════════════════════
// 6. OPEN CONSULT — READY→IN_CONSULT via the shared state machine (If-Match)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Flip an encounter into IN_CONSULT (the doctor opening the consult). Uses the
 * shared shape/opd-state.js state machine for the legal-transition check + the
 * Encounter/Provenance pair, then writes with If-Match optimistic concurrency.
 * READY→IN_CONSULT is the normal path; EXPIRED→IN_CONSULT (a returned walk-in)
 * is also legal per the table. Idempotent when already IN_CONSULT.
 *
 * On a 412 (a concurrent writer advanced the row) this does a PLAIN RETRY —
 * re-read the SAME encounter, rebuild the transition from its fresh state, and
 * write again (up to `retries` times). This differs from call-next, which on 412
 * moves to the NEXT candidate (see medplum-callin.js) — here there is exactly one
 * encounter the doctor asked to open, so we re-drive it, not skip it.
 *
 * Returns { state:'IN_CONSULT', encounter } — DTO parity with the Express leg
 * (consult.mjs:43). Throws BAD_TRANSITION (code on the error) when the fresh
 * state can't legally reach IN_CONSULT, matching the server (mapped to 400 there).
 *
 * @param {object} args
 * @param {string} args.encounterId  Medplum Encounter UUID
 * @param {string} [args.actor='Doctor']
 * @param {number} [args.retries=3]  max 412 re-reads
 */
export async function openConsult({ encounterId, actor, retries = 3 } = {}) {
  if (!encounterId) throw new Error('openConsult: encounterId is required');
  const { medplum } = await getMedplum();
  const target = STATES.IN_CONSULT;

  let attempt = 0;
  // Read → build → write, re-reading on 412 (plain retry on the same row).
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const enc = await medplum.readResource('Encounter', encounterId);
    if (currentState(enc) === target) return { state: target, encounter: enc };

    // Throws BAD_TRANSITION when the fresh state can't reach IN_CONSULT.
    const { resource, provenance } = buildTransitionUpdate(enc, target, { actor: actor || 'Doctor', activity: 'CONSULT_OPENED' });
    try {
      const saved = await updateWithIfMatch(medplum, resource);
      // Provenance is written AFTER the authoritative state write lands, so a
      // failed/retried transition never leaves an orphan Provenance.
      const provOut = { ...provenance };
      const bag = provOut._healther; delete provOut._healther;
      provOut.extension = [...(provOut.extension || []), bagExt(EXT_OPD_HEALTHER, bag)];
      try { await medplum.createResource(provOut); } catch (_) { /* audit is best-effort; the state write is the act */ }
      medplum.invalidateSearches('Encounter');
      return { state: target, encounter: saved };
    } catch (e) {
      if (is412(e) && attempt < retries) { attempt += 1; continue; }
      throw e;
    }
  }
}
